import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { EyeIcon } from "@heroicons/react/24/outline";
import { useUpdateLesson } from "../../api/mutations";
import { checkLessonSlug } from "../../api/client";
import { useSlugValidation } from "../../hooks/useSlugValidation";
import SlugInputField from "../../componentLibrary/SlugInputField";
import {
  useGetLesson,
  useGetLessonContainedTopics,
  useGetTopics,
} from "../../api/queries";
import {
  ComponentEnum,
  ComponentI,
  LessonI,
  BottomNavigationBarType,
} from "../../types";
import { useNotification } from "../../hooks/useNotification";
import { useComponentMinimization } from "../../hooks/useComponentMinimization";
import Container from "../../componentLibrary/Container";
import Input from "../../componentLibrary/Input";
import Button from "../../componentLibrary/Button";
import Spinner from "../../componentLibrary/Spinner";
import ComponentList from "../CreateLessonPage/components/ComponentList";
import PageList from "../CreateLessonPage/components/PageList";
import IconSelect from "../ContentManagementPage/components/IconSelect";
import PageComponentContainer from "../CreateLessonPage/components/PageComponentContainer";
import { ImageTypeEnum } from "../../../../server/src/types";
import SubmitAndPreviewButtons, {
  validateLesson,
} from "../CreateLessonPage/components/SubmitAndPreviewButtons";
import TabGroup from "../../componentLibrary/TabGroup";
import ConfirmationBox from "../../componentLibrary/ConfirmationBox";
import PasteComponentModal, {
  PasteWhere,
} from "../CreateLessonPage/components/PasteComponentModal";
import {
  SelectedComponentType,
  handlePastePreview,
  handleInsertComponent,
} from "../CreateLessonPage/utils/selectedComponentUtils";
import MultiSelectDropdown, {
  MultiSelectOptionI,
} from "../../componentLibrary/MultiSelectDropdown";
import LessonErrorModal from "../CreateLessonPage/components/LessonErrorModal";
import FloatingActionButton from "../../componentLibrary/FloatingActionButton";

// Tab indices for the right panel
const LESSON_INFO_TAB = 0;
const COMPONENTS_TAB = 2;

// Tab indices for the left panel
const LEFT_PAGES_TAB = 0;
const LEFT_COMPONENTS_TAB = 1;

// Session storage key for persisting tab state
const EDITOR_TAB_STORAGE_KEY = "lesson-editor-tab";

const blankLesson = {
  title: "",
  icon: { src: "", key: "", name: "", alt: "", type: ImageTypeEnum.ICON },
  pages: [{ _id: `page_${Date.now()}`, title: "", components: [] }],
};

export default function EditLessonPage() {
  const { state } = useLocation();
  const moduleId = state?.moduleId;
  const initialPageIndex = state?.pageIndex;

  const [currentPageId, setCurrentPageId] = useState<string>();
  const [selectedComponent, setSelectedComponent] =
    useState<SelectedComponentType>({
      component: null,
      parentComponent: null,
    });
  const [isPasteComponentOpen, setIsPasteComponentOpen] =
    useState<boolean>(false);
  const [pasteComponent, setPasteComponent] = useState<ComponentI | null>(null);
  const [lessonChanged, setLessonChanged] = useState(false);

  // Right panel tab state - read from sessionStorage if available, otherwise default to Lesson Info (0)
  const [rightPanelTab, setRightPanelTab] = useState<number>(() => {
    const storedTab = sessionStorage.getItem(EDITOR_TAB_STORAGE_KEY);
    if (storedTab !== null) {
      const parsed = parseInt(storedTab, 10);
      if (parsed >= LESSON_INFO_TAB && parsed <= COMPONENTS_TAB) {
        return parsed;
      }
    }
    return LESSON_INFO_TAB;
  });
  // Left panel tab state - sync with right panel if coming from Components tab
  const [leftPanelTab, setLeftPanelTab] = useState<number>(() => {
    const storedTab = sessionStorage.getItem(EDITOR_TAB_STORAGE_KEY);
    if (storedTab !== null) {
      const parsed = parseInt(storedTab, 10);
      // If right panel is on Components tab, set left panel to Components as well
      if (parsed === COMPONENTS_TAB) {
        return LEFT_COMPONENTS_TAB;
      }
    }
    return LEFT_PAGES_TAB;
  });
  // Track whether this is the initial page load to set proper defaults
  const isInitialPageSet = useRef(false);
  // Track previously visited pages to determine if a page is "new"
  const visitedPageIds = useRef<Set<string>>(new Set());

  // Handle left panel tab change - sync with right panel
  const handleLeftPanelTabChange = (index: number) => {
    setLeftPanelTab(index);
    if (index === LEFT_COMPONENTS_TAB) {
      setRightPanelTab(COMPONENTS_TAB);
    }
  };

  const componentRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>(
    {},
  );

  const handleComponentClick = (clickedComponent: SelectedComponentType) => {
    if (
      clickedComponent?.component?._id === selectedComponent?.component?._id
    ) {
      setSelectedComponent({
        component: null,
        parentComponent: null,
      });
    } else {
      setSelectedComponent(clickedComponent);
    }
  };

  const [updatedLesson, setUpdatedLesson] = useState<LessonI>(blankLesson);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isViewLessonConfirmOpen, setIsViewLessonConfirmOpen] = useState(false);
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const { notify } = useNotification();

  const {
    status: slugStatus,
    validate: validateSlug,
    reset: resetSlugStatus,
  } = useSlugValidation(checkLessonSlug, lessonId);

  const { data: lesson, isError, isLoading } = useGetLesson(lessonId);

  const {
    data: modules,
    isLoading: modulesLoading,
    isError: modulesError,
  } = useGetTopics();

  const { data: lessonContainedModules = [] } =
    useGetLessonContainedTopics(lessonId);
  const [linkedModules, setLinkedModules] = useState<MultiSelectOptionI[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);

  useEffect(() => {
    if (lessonContainedModules) {
      setLinkedModules(
        lessonContainedModules.map((module) => ({
          value: module._id,
          label: module.title,
        })),
      );
    }
  }, [lessonContainedModules?.map((m) => m._id).join(",")]);

  const queryClient = useQueryClient();

  const mutation = useUpdateLesson({
    onSuccess: async () => {
      notify("success", "Lesson updated successfully");
      await queryClient.invalidateQueries({ queryKey: ["lessons"] });
    },
    onError: async () => {
      notify("error", "There was an error updating the lesson.");
    },
  });

  const handleBack = () => {
    if (!lessonChanged) {
      navigate("/admin/content-management", { state: { moduleId: moduleId } });
      return;
    }
    setIsConfirmationOpen(true);
  };

  const handleSaveAndExit = () => {
    if (!validateLesson(updatedLesson, onLessonErrorCallback)) {
      notify("error", "Please Resolve the errors");
      setIsConfirmationOpen(false);
      return;
    }
    mutation.mutate({
      lesson: updatedLesson,
      modules: linkedModules.map((module) => module.value),
    });
    setIsConfirmationOpen(false);
    navigate("/admin/content-management", { state: { moduleId: moduleId } });
  };

  const handleDiscardAndExit = () => {
    setIsConfirmationOpen(false);
    navigate("/admin/content-management", { state: { moduleId: moduleId } });
  };

  const handleCancel = () => {
    setIsConfirmationOpen(false);
  };

  // View in Lesson handlers
  const getLessonUrl = () => {
    const currentPageIndex = updatedLesson.pages.findIndex(
      (page) => page._id === currentPageId,
    );
    return `/topics/${moduleId}/sections/${lessonId}?page=${currentPageIndex >= 0 ? currentPageIndex : 0}`;
  };

  const handleViewInLesson = () => {
    if (!lessonChanged) {
      navigate(getLessonUrl());
      return;
    }
    setIsViewLessonConfirmOpen(true);
  };

  const handleSaveAndViewLesson = () => {
    if (!validateLesson(updatedLesson, onLessonErrorCallback)) {
      notify("error", "Please resolve the errors before saving");
      setIsViewLessonConfirmOpen(false);
      return;
    }
    mutation.mutate({
      lesson: updatedLesson,
      modules: linkedModules.map((module) => module.value),
    });
    setIsViewLessonConfirmOpen(false);
    navigate(getLessonUrl());
  };

  const handleDiscardAndViewLesson = () => {
    setIsViewLessonConfirmOpen(false);
    navigate(getLessonUrl());
  };

  const handleCancelViewLesson = () => {
    setIsViewLessonConfirmOpen(false);
  };

  useEffect(() => {
    if (lesson) {
      if (!currentPageId) {
        // Use initialPageIndex from navigation state if provided, otherwise default to first page
        const startPageIndex =
          initialPageIndex !== undefined &&
          initialPageIndex >= 0 &&
          initialPageIndex < lesson.pages.length
            ? initialPageIndex
            : 0;
        setCurrentPageId(lesson.pages[startPageIndex]._id);
        // Only reset to Lesson Info tab if NOT navigating from LessonPage (no initialPageIndex)
        // When coming from LessonPage (pencil icon), keep the stored tab from sessionStorage
        if (initialPageIndex === undefined) {
          setRightPanelTab(LESSON_INFO_TAB);
        }
        isInitialPageSet.current = true;
        visitedPageIds.current.add(lesson.pages[startPageIndex]._id);
      }
      setUpdatedLesson(lesson);
      if (!lesson.bottomNavigationBarType) {
        setUpdatedLesson((prev) => ({
          ...prev,
          bottomNavigationBarType: BottomNavigationBarType.MULTI_PAGE,
        }));
      }
    }
  }, [lesson]);

  // Keep track of visited pages (no longer auto-switching tabs)
  useEffect(() => {
    if (!currentPageId || !isInitialPageSet.current) return;
    visitedPageIds.current.add(currentPageId);
  }, [currentPageId]);

  // Persist right panel tab to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem(EDITOR_TAB_STORAGE_KEY, rightPanelTab.toString());
  }, [rightPanelTab]);

  const handleFocusComponent = (component: ComponentI | null) => {
    if (component && componentRefs.current[component._id]) {
      componentRefs.current[component._id]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const tabData = [
    {
      label: "Pages",
      content: (
        <PageList
          lesson={updatedLesson}
          setLesson={setUpdatedLesson}
          currentPageId={currentPageId}
          setCurrentPageId={setCurrentPageId}
        />
      ),
    },
    {
      label: "Components",
      content: (
        <ComponentList
          lesson={updatedLesson}
          setLesson={setUpdatedLesson}
          currentPageId={currentPageId}
          selectedComponent={selectedComponent}
          setSelectedComponent={setSelectedComponent}
          handleFocusComponent={handleFocusComponent}
        />
      ),
    },
  ];

  const handlePasteComponent = (
    component: ComponentI,
    pasteWhere?: PasteWhere,
  ) => {
    handleInsertComponent(
      updatedLesson,
      setUpdatedLesson,
      currentPageId,
      component,
      selectedComponent,
      pasteWhere,
      setSelectedComponent,
      handleFocusComponent,
      notify,
    );
  };

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [lessonErrors, setLessonErrors] = useState<{ text: string }[]>([]);
  const [totalPageErrors, setTotalPageErrors] = useState<
    {
      pageTitle: string;
      pageErrors: {
        text: string;
        pageId: string;
        componentId: string;
      }[];
    }[]
  >([]);

  const onCloseErrorModal = () => {
    setIsErrorModalOpen(false);
    setLessonErrors([]);
    setTotalPageErrors([]);
  };

  const onLessonErrorCallback = (
    lessonErrors: { text: string }[],
    totalPageErrors: {
      pageTitle: string;
      pageErrors: {
        text: string;
        pageId: string;
        componentId: string;
      }[];
    }[],
  ) => {
    setIsErrorModalOpen(true);
    setLessonErrors(lessonErrors);
    setTotalPageErrors(totalPageErrors);
  };

  const onErrorClick = (pageId, componentId) => {
    setCurrentPageId(pageId);
    if (componentId) {
      const page = updatedLesson.pages.find((p) => p._id === pageId);
      if (page) {
        const component = page.components.find((c) => c._id === componentId);
        if (component) {
          setSelectedComponent({
            component,
            parentComponent: null,
          });
          setTimeout(() => {
            handleFocusComponent(component);
          }, 200);
        }
      }
    }
  };

  // Compute currentPage and call useComponentMinimization before early returns -
  // hooks must run unconditionally (Rules of Hooks)
  const currentPageIndex = updatedLesson.pages.findIndex(
    (page) => page._id === currentPageId,
  );
  const currentPage = updatedLesson.pages[currentPageIndex];

  const {
    minimizedComponents,
    setMinimizedComponents,
    handleMinimizeAll,
    handleExpandAll,
    areAllMinimized,
  } = useComponentMinimization(currentPage);

  if (isLoading || modulesLoading) return <Spinner className="mx-auto" />;
  if (isError || !lesson || modulesError || !modules) return <div>Error</div>;

  // Extract input variables from pages up to the current page
  const variablesByPage = updatedLesson.pages.map((page) =>
    page.components
      .flatMap((component) =>
        component.type === ComponentEnum.LOGIC_COMPONENT
          ? (component.content?.componentsList?.map(
              (comp) => comp.content.variable,
            ) ?? [])
          : component.content.variable
            ? [component.content.variable]
            : [],
      )
      .filter(Boolean),
  );

  const inputVariablesUpToCurrentPage = variablesByPage
    .slice(0, currentPageIndex + 1)
    .flat();

  const inputVariablesUpToPreviousPage = variablesByPage
    .slice(0, currentPageIndex)
    .flat();

  return (
    <div className="flex flex-col flex-grow">
      <div className="px-2 mb-1 mt-1">
        <SubmitAndPreviewButtons
          lesson={updatedLesson}
          mutation={mutation}
          selectedComponent={selectedComponent}
          handlePastePreview={() =>
            handlePastePreview(
              setPasteComponent,
              setIsPasteComponentOpen,
              notify,
            )
          }
          linkedModules={linkedModules.map((module) => module.value)}
          currentPageId={currentPageId}
          setCurrentPageId={setCurrentPageId}
          setLessonChanged={setLessonChanged}
          handleBackFunction={handleBack}
          onLessonErrorCallback={onLessonErrorCallback}
          onViewInLesson={moduleId ? handleViewInLesson : undefined}
          onMinimizeAll={handleMinimizeAll}
          onExpandAll={handleExpandAll}
          areAllMinimized={areAllMinimized}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 px-2 md:px-4">
        <div className="md:col-span-5 lg:col-span-4 xl:col-span-3 w-full flex flex-col min-h-0 max-h-[65vh] md:max-h-[74vh]">
          <Container className="flex flex-col flex-1 min-h-0 w-full px-1 md:px-6 overflow-hidden">
            <TabGroup
              tabs={tabData}
              selectedIndex={leftPanelTab}
              onTabChange={handleLeftPanelTabChange}
            />
          </Container>
        </div>
        <div className="md:col-span-7 lg:col-span-8 xl:col-span-9 flex flex-col h-full space-y-2">
          <Container className="flex flex-col flex-grow space-y-2 max-h-[65vh] md:max-h-[74vh] overflow-hidden px-1 md:px-6">
            <TabGroup
              tabs={[
                {
                  label: "Lesson Info",
                  content: (
                    <div className="space-y-4 overflow-y-auto h-full p-1">
                      <h3 className="mb-2">Lesson</h3>
                      <Input
                        label="Title"
                        value={updatedLesson.title}
                        onChange={(e) => {
                          setLessonChanged(true);
                          setUpdatedLesson({
                            ...updatedLesson,
                            title: e.target.value,
                          });
                        }}
                        autoFocus
                      />
                      <SlugInputField
                        value={updatedLesson.slug || ""}
                        onChange={(slug) => {
                          setLessonChanged(true);
                          setUpdatedLesson((prev) => ({ ...prev, slug }));
                        }}
                        onBlur={validateSlug}
                        onResetStatus={resetSlugStatus}
                        title={updatedLesson.title}
                        urlPrefix="/sections/"
                        slugStatus={slugStatus}
                      />
                      <MultiSelectDropdown
                        label="Linked Modules"
                        options={modules.map((module) => ({
                          value: module._id,
                          label: module.title,
                        }))}
                        value={linkedModules}
                        onChange={(value) => {
                          setLessonChanged(true);
                          setLinkedModules(value);
                        }}
                      />
                      <IconSelect
                        icon={updatedLesson.icon}
                        setIcon={(icon) => {
                          setLessonChanged(true);
                          setUpdatedLesson((prev) => ({ ...prev, icon }));
                        }}
                      />
                      <div className="flex flex-row justify-between items-center">
                        <div>Show Bottom Navigation Bar</div>
                        <div className="flex flex-row gap-2">
                          <Button
                            onClick={() =>
                              setUpdatedLesson((prev) => ({
                                ...prev,
                                bottomNavigationBarType:
                                  BottomNavigationBarType.MULTI_PAGE,
                              }))
                            }
                            variant={
                              updatedLesson.bottomNavigationBarType ===
                              BottomNavigationBarType.MULTI_PAGE
                                ? "primary"
                                : "secondary"
                            }
                          >
                            Multi Page
                          </Button>
                          <Button
                            onClick={() =>
                              setUpdatedLesson((prev) => ({
                                ...prev,
                                bottomNavigationBarType:
                                  BottomNavigationBarType.SINGLE_PAGE,
                              }))
                            }
                            variant={
                              updatedLesson.bottomNavigationBarType ===
                              BottomNavigationBarType.SINGLE_PAGE
                                ? "primary"
                                : "secondary"
                            }
                          >
                            Single Page
                          </Button>
                          <Button
                            onClick={() =>
                              setUpdatedLesson((prev) => ({
                                ...prev,
                                bottomNavigationBarType:
                                  BottomNavigationBarType.NONE,
                              }))
                            }
                            variant={
                              updatedLesson.bottomNavigationBarType ===
                              BottomNavigationBarType.NONE
                                ? "primary"
                                : "secondary"
                            }
                          >
                            None
                          </Button>
                        </div>
                      </div>
                    </div>
                  ),
                },
                {
                  label: "Page Info",
                  content: currentPageId ? (
                    <div className="flex flex-col flex-grow bg-[#F5F5F5] rounded-lg p-4 space-y-2 shadow-md border border-[#E0E0E0] overflow-y-auto h-full">
                      <PageComponentContainer
                        lesson={updatedLesson}
                        setLesson={setUpdatedLesson}
                        currentPageId={currentPageId}
                        selectedComponent={selectedComponent}
                        handleComponentClick={handleComponentClick}
                        variableUptoCurrentPage={inputVariablesUpToCurrentPage}
                        variableUptoPreviousPage={
                          inputVariablesUpToPreviousPage
                        }
                        setWarnings={setWarnings}
                        setLessonChanged={setLessonChanged}
                        componentRefs={componentRefs}
                        mode="page-info"
                        minimizedComponents={minimizedComponents}
                        setMinimizedComponents={setMinimizedComponents}
                      />
                    </div>
                  ) : (
                    <div className="text-gray-500 text-center py-8">
                      Select a page to view its info
                    </div>
                  ),
                },
                {
                  label: "Components",
                  content: currentPageId ? (
                    <div className="flex flex-col flex-grow bg-[#F5F5F5] rounded-lg p-4 space-y-2 shadow-md border border-[#E0E0E0] overflow-y-auto h-full">
                      <PageComponentContainer
                        lesson={updatedLesson}
                        setLesson={setUpdatedLesson}
                        currentPageId={currentPageId}
                        selectedComponent={selectedComponent}
                        handleComponentClick={handleComponentClick}
                        variableUptoCurrentPage={inputVariablesUpToCurrentPage}
                        variableUptoPreviousPage={
                          inputVariablesUpToPreviousPage
                        }
                        setWarnings={setWarnings}
                        setLessonChanged={setLessonChanged}
                        componentRefs={componentRefs}
                        mode="components"
                        minimizedComponents={minimizedComponents}
                        setMinimizedComponents={setMinimizedComponents}
                      />
                      {warnings?.length > 0 && (
                        <div className="mb-4">
                          {warnings.map((warning, index) => (
                            <div
                              key={index}
                              className="text-yellow-600 text-sm"
                            >
                              ⚠️ {warning}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-gray-500 text-center py-8">
                      Select a page to view its components
                    </div>
                  ),
                },
              ]}
              selectedIndex={rightPanelTab}
              onTabChange={setRightPanelTab}
            />
          </Container>
        </div>
      </div>
      <ConfirmationBox
        isOpen={isConfirmationOpen}
        title="Save Changes"
        message="Do you want to save your changes before leaving?"
        confirmText="Save & Exit"
        cancelText="Discard & Exit"
        onConfirm={handleSaveAndExit}
        onCancel={handleDiscardAndExit}
        onCloseBox={handleCancel}
      />
      <ConfirmationBox
        isOpen={isViewLessonConfirmOpen}
        title="Save Changes"
        message="Do you want to save your changes before viewing the lesson?"
        confirmText="Save & View"
        cancelText="Discard & View"
        onConfirm={handleSaveAndViewLesson}
        onCancel={handleDiscardAndViewLesson}
        onCloseBox={handleCancelViewLesson}
      />
      <PasteComponentModal
        isModalOpen={isPasteComponentOpen}
        setIsModalOpen={setIsPasteComponentOpen}
        pasteComponent={pasteComponent}
        setPasteComponent={setPasteComponent}
        handlePasteComponent={handlePasteComponent}
        selectedComponent={selectedComponent}
      />
      <LessonErrorModal
        isModalOpen={isErrorModalOpen}
        onCloseModal={onCloseErrorModal}
        lessonErrors={lessonErrors}
        totalPageErrors={totalPageErrors}
        onErrorClick={onErrorClick}
      />
      {selectedComponent?.component && (
        <FloatingActionButton
          variant="primary"
          onClick={() => handleFocusComponent(selectedComponent?.component)}
          className="p-2"
          title="Move to Selected Component"
        >
          <EyeIcon className="w-8 h-8" />
        </FloatingActionButton>
      )}
    </div>
  );
}
